/**
 * MessagePhaseManager - Handles message evolution through phases
 *
 * Implements Claude Desktop pattern: single message evolves through:
 * thinking → tool_executing → tool_results → response
 */

import { getToolDescription } from './toolDisplayConfig';

export const MessagePhase = {
  THINKING: 'thinking',
  TOOL_EXECUTING: 'tool_executing',
  TOOL_RESULTS: 'tool_results',
  RESPONSE: 'response',
  ERROR: 'error',
  STOPPED: 'stopped'
};

/**
 * The phases that render a live loading indicator (the blue ripple / tool one-liner).
 * Single source of truth for "this message is currently showing a loader", shared by the
 * list-level loader scoping in AgentChatMessageList and the Stop teardown in
 * AgentCoreAdapter so the two can't drift (TD-2383).
 * @param {string} phase - A MessagePhase value
 * @returns {boolean} True if a message in this phase renders a loading indicator
 */
export function phaseRendersLoader(phase) {
  return phase === MessagePhase.THINKING || phase === MessagePhase.TOOL_EXECUTING;
}

/**
 * Detect context overflow errors from backend error messages.
 * Used by AgentCoreAdapter (stash/retry) and MessagePhaseManager (user-friendly message).
 */
export function isContextOverflowError(message) {
  return message?.includes('limit of what I can track') || message?.includes('conversation has grown too long');
}

export const PhaseConfig = {
  [MessagePhase.THINKING]: {
    displayText: "Thinking...",
    icon: "💭",
    style: "thinking"
  },

  [MessagePhase.TOOL_EXECUTING]: {
    displayText: (toolName) => getToolDescription(toolName),
    icon: "🔧",
    style: "tool-executing"
  },

  [MessagePhase.TOOL_RESULTS]: {
    displayText: null, // Content from toolResults
    icon: "📊",
    style: "tool-results",
    preserveResults: true
  },

  [MessagePhase.RESPONSE]: {
    displayText: (content) => content,
    icon: null,
    style: "response",
    showResults: true // Keep tool results visible
  },

  [MessagePhase.ERROR]: {
    displayText: (errorMsg) => errorMsg || "An error occurred",
    icon: "❌",
    style: "error",
    showRetry: true,
    showError: true
  },

  [MessagePhase.STOPPED]: {
    displayText: "Stopped by user",
    icon: "", // blue square rendered via CSS (.phase-stopped .phase-text::before)
    style: "stopped",
    showSpinner: false
  }
};

class MessagePhaseManager {
  constructor() {
    this.phaseChangeCallbacks = [];
    this.messageUpdateCallbacks = [];
  }

  /**
   * Create a new message in thinking phase
   * @param {string} sessionId - Session identifier
   * @param {string} messageType - 'ai' | 'user' | 'system'
   * @returns {Object} New message object
   */
  createThinkingMessage(sessionId, messageType = 'ai') {
    const message = {
      id: crypto.randomUUID(),
      type: messageType,
      sessionId,

      // PHASE SYSTEM
      phase: MessagePhase.THINKING,
      content: "Thinking...",
      isComplete: false,

      // TIMESTAMPS
      timestamp: new Date(),
      phaseStartTime: new Date(),

      // TOOL EXECUTION STATE
      toolName: null,
      toolParams: null,
      toolResults: null,

      // STREAMING STATE
      contentChunks: [],
      streamErrors: [],

      // FINAL CONTENT
      finalContent: null,

      // BACKWARD COMPATIBILITY
      contentType: 'text',
      tableData: null // Will be populated from toolResults
    };

    this._notifyMessageUpdate(sessionId, message, 'created');

    return message;
  }

  /**
   * Transition message back to thinking phase (for retries)
   * @param {Object} message - Message to transition
   */
  transitionToThinking(message) {
    message.phase = MessagePhase.THINKING;
    message.content = PhaseConfig[MessagePhase.THINKING].displayText;
    message.isComplete = false;
    message.error = null; // Clear error state
    message.phaseStartTime = new Date();

    this._notifyPhaseChange(message, MessagePhase.THINKING);
    this._notifyMessageUpdate(message.sessionId, message, 'retry');
  }

  /**
   * Transition message to tool execution phase
   * @param {Object} message - Message to transition
   * @param {string} toolName - Name of tool being executed
   * @param {Object} toolParams - Tool parameters
   */
  transitionToToolExecution(message, toolName, toolParams = {}) {
    // Trust AgentCore - no validation, just transition

    message.phase = MessagePhase.TOOL_EXECUTING;
    message.content = PhaseConfig[MessagePhase.TOOL_EXECUTING].displayText(toolName);
    message.toolName = toolName;
    message.toolParams = toolParams;
    // phaseStartTime doubles as the loading-row elapsed-timer start: it is set on entry to
    // TOOL_EXECUTING and only read while the phase stays TOOL_EXECUTING (see AgentAIMessage).
    message.phaseStartTime = new Date();

    this._notifyPhaseChange(message, MessagePhase.TOOL_EXECUTING);
    this._notifyMessageUpdate(message.sessionId, message, 'phase_changed');
  }

  /**
   * Transition message to tool results phase
   * @param {Object} message - Message to transition
   * @param {Object} toolResults - Tool execution results
   */
  transitionToToolResults(message, toolResults) {
    // Trust AgentCore - no validation, just transition

    message.phase = MessagePhase.TOOL_RESULTS;
    message.content = null; // Content comes from toolResults
    message.toolResults = toolResults;
    message.phaseStartTime = new Date();

    // BACKWARD COMPATIBILITY: Set contentType and tableData for existing components
    if (toolResults.type === 'table') {
      message.contentType = 'table';
      message.tableData = toolResults.data;
    }

    this._notifyPhaseChange(message, MessagePhase.TOOL_RESULTS);
    this._notifyMessageUpdate(message.sessionId, message, 'phase_changed');
  }

  /**
   * Start streaming response phase (SSE streaming lifecycle).
   * Sets phase to RESPONSE with isComplete=false.
   * Idempotent — safe to call on every text event.
   *
   * @param {Object} message - Message to transition
   */
  startStreamingResponse(message) {
    if (message.phase !== MessagePhase.RESPONSE || message.isComplete) {
      message.phase = MessagePhase.RESPONSE;
      message.isComplete = false;
      message.phaseStartTime = new Date();

      this._notifyPhaseChange(message, MessagePhase.RESPONSE);
      this._notifyMessageUpdate(message.sessionId, message, 'streaming_started');
    }
  }

  /**
   * Finalize streaming response (SSE streaming lifecycle).
   * Sets isComplete=true. Optionally overrides content with finalContent.
   *
   * @param {Object} message - Message to finalize
   * @param {string} [finalContent] - Optional final content override
   */
  finalizeStreamingResponse(message, finalContent) {
    if (finalContent === undefined) {
      message.finalContent = message.content;
    } else {
      message.content = finalContent;
      message.finalContent = finalContent;
    }

    message.isComplete = true;

    this._notifyMessageUpdate(message.sessionId, message, 'completed');
  }

  /**
   * Transition message to final response phase
   * @param {Object} message - Message to transition
   * @param {string} finalContent - Final AI response text
   */
  transitionToResponse(message, finalContent) {
    // Trust AgentCore - no validation, just transition


    message.phase = MessagePhase.RESPONSE;
    message.content = finalContent;
    message.finalContent = finalContent;
    message.isComplete = true;
    message.phaseStartTime = new Date();

    this._notifyPhaseChange(message, MessagePhase.RESPONSE);
    this._notifyMessageUpdate(message.sessionId, message, 'completed');
  }

  /**
   * Handle streaming content updates (for response phase)
   * @param {Object} message - Message to update
   * @param {string} contentChunk - New content chunk
   */
  updateStreamingContent(message, contentChunk) {
    if (message.phase !== MessagePhase.RESPONSE || message.isComplete) {
      return;
    }

    message.contentChunks.push({
      chunk: contentChunk,
      timestamp: new Date()
    });

    message.content = message.contentChunks.map(c => c.chunk).join('');

    this._notifyMessageUpdate(message.sessionId, message, 'streaming_update');
  }

  /**
   * Transition message to error phase
   * @param {Object} message - Message to transition
   * @param {Error} error - The error that occurred
   * @param {Object} options - Error handling options
   */
  transitionToError(message, error, options = {}) {
    const errorDetails = {
      message: error.message || 'An unexpected error occurred',
      name: error.name || 'Error',
      timestamp: new Date(),
      canRetry: options.canRetry !== false, // Default to retryable
      originalPhase: message.phase,
      userFriendlyMessage: this._getUserFriendlyErrorMessage(error)
    };

    message.phase = MessagePhase.ERROR;
    message.content = errorDetails.userFriendlyMessage;
    message.error = errorDetails;
    message.isComplete = true;
    message.phaseStartTime = new Date();

    this._notifyPhaseChange(message, MessagePhase.ERROR);
    this._notifyMessageUpdate(message.sessionId, message, 'error', error);
  }

  /**
   * Finalize a message that the user explicitly stopped (TD-2347).
   * Marks it complete with a "Stopped by user" label. Tool state (toolName,
   * toolResults) is retained so the UI can still reflect what completed.
   * @param {Object} message - Message to transition
   */
  transitionToStopped(message) {
    // Preserve any partial response already streamed (R1: a "Stopped by user"
    // inline note, not a wipe). Only fall back to the bare label when nothing
    // meaningful was shown yet (e.g. still in the thinking/tool phase).
    const hasPartial =
      typeof message.content === 'string' &&
      message.content.trim().length > 0 &&
      message.content !== PhaseConfig[MessagePhase.THINKING].displayText;
    message.phase = MessagePhase.STOPPED;
    if (!hasPartial) {
      message.content = PhaseConfig[MessagePhase.STOPPED].displayText;
    }
    message.isComplete = true;
    message.stoppedByUser = true;
    message.phaseStartTime = new Date();

    this._notifyPhaseChange(message, MessagePhase.STOPPED);
    this._notifyMessageUpdate(message.sessionId, message, 'stopped');
  }

  /**
   * Handle streaming errors with partial recovery
   * @param {Object} message - Message with error
   * @param {Error} error - The error that occurred
   */
  handleStreamingError(message, error) {
    message.streamErrors.push({
      error: error.message,
      timestamp: new Date(),
      phase: message.phase
    });

    // If we have partial content, complete the message with what we have
    if (message.content && message.content.length > 0) {
      message.isComplete = true;
      message.finalContent = message.content;
      this._notifyMessageUpdate(message.sessionId, message, 'partial_recovery', error);
    } else {
      // No partial content - transition to error state
      this.transitionToError(message, error);
    }
  }

  /**
   * Get user-friendly error message based on error type
   * @param {Error} error - The error object
   * @returns {string} User-friendly error message
   */
  _getUserFriendlyErrorMessage(error) {
    // Handle specific AWS/AgentCore errors
    if (error.name === 'MCPClientInitializationError' || error.message?.includes('MCP')) {
      return 'The AI agent is temporarily unavailable due to high demand. Please try again in a moment.';
    }

    if (error.name === 'ThrottlingException' || error.message?.includes('429')) {
      return 'The AI agent is busy processing other requests. Please wait a moment and try again.';
    }

    if (error.$metadata?.httpStatusCode >= 500) {
      return 'The AI service is experiencing technical difficulties. Please try again shortly.';
    }

    if (isContextOverflowError(error.message)) {
      return 'This conversation has reached its memory limit. Please start a new chat to continue.';
    }

    if (error.name === 'ValidationException') {
      return 'There was an issue with your request. Please try rephrasing your message.';
    }

    if (error.message?.includes('token') || error.message?.includes('auth')) {
      return 'Authentication expired. Please refresh the page and try again.';
    }

    // Default user-friendly message
    return 'Sorry, I encountered an issue processing your request. Please try again.';
  }

  /**
   * Check if message is in a streaming phase
   * @param {Object} message - Message to check
   * @returns {boolean} True if message is actively streaming
   */
  isStreaming(message) {
    return !message.isComplete && (
      message.phase === MessagePhase.THINKING ||
      message.phase === MessagePhase.TOOL_EXECUTING ||
      message.phase === MessagePhase.RESPONSE
    );
  }

  /**
   * Get display content for message based on phase
   * @param {Object} message - Message to get display content for
   * @returns {Object} Display content object
   */
  getDisplayContent(message) {
    const config = PhaseConfig[message.phase];

    switch (message.phase) {
      case MessagePhase.THINKING:
        return {
          type: 'status',
          text: config.displayText,
          icon: config.icon
        };

      case MessagePhase.TOOL_EXECUTING:
        return {
          type: 'status',
          text: message.content,
          icon: config.icon
        };

      case MessagePhase.TOOL_RESULTS:
        return {
          type: 'tool_results',
          toolResults: message.toolResults,
          icon: config.icon
        };

      case MessagePhase.RESPONSE:
        return {
          type: 'response',
          text: message.content,
          toolResults: message.toolResults, // Preserve tool results
          showResults: config.showResults
        };

      case MessagePhase.ERROR:
        return {
          type: 'error',
          text: message.content,
          error: message.error,
          icon: config.icon,
          showRetry: config.showRetry && message.error?.canRetry !== false
        };

      case MessagePhase.STOPPED:
        // If partial content was preserved, render it as a response with a
        // "stopped" note; otherwise show the bare status label.
        return message.content && message.content !== config.displayText
          ? {
              type: 'response',
              text: message.content,
              toolResults: message.toolResults,
              showResults: true,
              stopped: true
            }
          : {
              type: 'status',
              text: config.displayText,
              icon: config.icon,
              showSpinner: false
            };

      default:
        // Backward compatibility for non-phased messages
        return {
          type: 'text',
          text: message.content
        };
    }
  }

  // ============================================================================
  // EVENT SYSTEM
  // ============================================================================

  onPhaseChange(callback) {
    this.phaseChangeCallbacks.push(callback);
  }

  onMessageUpdate(callback) {
    this.messageUpdateCallbacks.push(callback);
  }

  _notifyPhaseChange(message, newPhase) {
    this.phaseChangeCallbacks.forEach(callback => {
      try {
        callback({
          type: 'phase_change',
          messageId: message.id,
          sessionId: message.sessionId,
          oldPhase: message.previousPhase || null,
          newPhase,
          timestamp: new Date()
        });
      } catch (error) {
        console.error('Error in phase change callback:', error);
      }
    });
  }

  _notifyMessageUpdate(sessionId, message, updateType, error = null) {
    this.messageUpdateCallbacks.forEach(callback => {
      try {
        callback({
          type: 'message_update',
          updateType,
          sessionId,
          messageId: message.id,
          message,
          error,
          timestamp: new Date()
        });
      } catch (error) {
        console.error('Error in message update callback:', error);
      }
    });
  }
}

// Export singleton instance
export const messagePhaseManager = new MessagePhaseManager();
export default messagePhaseManager;
